import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  MapIcon,
  PlusIcon,
  TrashIcon,
  MapPinIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Alert } from '@/components/ui/Alert';
import { gpsApi, Geofence } from '@/api/gpsApi';
import toast from 'react-hot-toast';

// Geofence creation schema
const geofenceSchema = z.object({
  geofencename: z.string().min(1, 'Geofence name is required'),
  geofencetype: z.enum(['2', '3'], {
    errorMap: () => ({ message: 'Please select geofence type' })
  }),

  // Circle parameters
  centerLatitude: z.number().optional(),
  centerLongitude: z.number().optional(),
  radius: z.number().min(100).max(5000).optional(),

  // Polygon parameters (simplified - 4 corners)
  polygonPoints: z.array(z.object({
    latitude: z.number(),
    longitude: z.number()
  })).optional(),

  // Advanced settings
  geofencespeeding: z.string().optional(),
  geofenceusetimes: z.string().optional(),

  // School-specific settings
  zoneType: z.enum(['SCHOOL', 'PICKUP_POINT', 'RESTRICTED_AREA', 'SPEED_ZONE']),
  priority: z.enum(['HIGH', 'MEDIUM', 'LOW']).default('MEDIUM'),
  description: z.string().optional()
});

type GeofenceFormData = z.infer<typeof geofenceSchema>;

const GeofenceManagement: React.FC = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedGeofence, setSelectedGeofence] = useState<Geofence | null>(null);
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isValid }
  } = useForm<GeofenceFormData>({
    resolver: zodResolver(geofenceSchema),
    defaultValues: {
      geofencetype: '2', // Circle by default
      radius: 500,
      zoneType: 'SCHOOL',
      priority: 'MEDIUM'
    }
  });

  const watchGeofenceType = watch('geofencetype');

  // Fetch geofences
  const { data: geofencesData, isLoading: geofencesLoading } = useQuery({
    queryKey: ['geofences'],
    queryFn: () => gpsApi.getGeofenceList(1, 100)
  });

  // Create geofence mutation
  const createMutation = useMutation({
    mutationFn: (data: GeofenceFormData) => {
      if (data.geofencetype === '2') {
        // Circle geofence
        const parameter = `${data.centerLongitude},${data.centerLatitude},${data.radius}`;
        return gpsApi.createAdvancedGeofence({
          geofencename: data.geofencename,
          geofencetype: 2,
          geofenceparameter: parameter,
          geofencespeeding: data.geofencespeeding,
          geofenceusetimes: data.geofenceusetimes
        });
      } else {
        // Polygon geofence
        const parameter = data.polygonPoints
          ?.map(point => `${point.longitude},${point.latitude}`)
          .join(';') || '';
        return gpsApi.createAdvancedGeofence({
          geofencename: data.geofencename,
          geofencetype: 3,
          geofenceparameter: parameter,
          geofencespeeding: data.geofencespeeding,
          geofenceusetimes: data.geofenceusetimes
        });
      }
    },
    onSuccess: () => {
      toast.success('Geofence created successfully');
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      setShowCreateForm(false);
      reset();
    },
    onError: () => {
      toast.error('Failed to create geofence');
    }
  });

  // Delete geofence mutation
  const deleteMutation = useMutation({
    mutationFn: (geofenceId: string) => gpsApi.deleteGeofence(geofenceId),
    onSuccess: () => {
      toast.success('Geofence deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
    },
    onError: () => {
      toast.error('Failed to delete geofence');
    }
  });

  // Predefined school locations for quick setup
  const presetLocations = [
    {
      name: 'Main School Building',
      latitude: -1.286389,
      longitude: 36.817223,
      radius: 200
    },
    {
      name: 'Sports Field',
      latitude: -1.286500,
      longitude: 36.817400,
      radius: 150
    },
    {
      name: 'Parking Lot',
      latitude: -1.286200,
      longitude: 36.817000,
      radius: 100
    },
    {
      name: 'School Gate',
      latitude: -1.286100,
      longitude: 36.817100,
      radius: 50
    }
  ];

  const handlePresetLocation = (preset: typeof presetLocations[0]) => {
    setValue('geofencename', preset.name);
    setValue('centerLatitude', preset.latitude);
    setValue('centerLongitude', preset.longitude);
    setValue('radius', preset.radius);
    setValue('zoneType', 'SCHOOL');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <MapIcon className="w-8 h-8 text-purple-600 mr-3" />
                Geofence Management
              </h1>
              <p className="text-gray-600 mt-2">
                Create and manage virtual boundaries for enhanced school security
              </p>
            </div>

            <Button
              onClick={() => setShowCreateForm(true)}
              className="px-6"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Geofence
            </Button>
          </div>
        </div>

        {/* Geofence Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <MapIcon className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Geofences</p>
                <p className="text-2xl font-bold text-gray-900">
                  {geofencesData?.data.geofences?.length || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <ShieldCheckIcon className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Zones</p>
                <p className="text-2xl font-bold text-gray-900">
                  {geofencesData?.data.geofences?.filter(g => g.schedule.allDay || g.schedule.timePeriods.length > 0).length || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Speed Zones</p>
                <p className="text-2xl font-bold text-gray-900">
                  {geofencesData?.data.geofences?.filter(g => g.speeding.enabled).length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Geofences List */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Active Geofences</h2>
          </div>

          <div className="p-6">
            {geofencesLoading ? (
              <LoadingSpinner />
            ) : geofencesData?.data.geofences && geofencesData.data.geofences.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {geofencesData.data.geofences.map((geofence: Geofence) => (
                  <div key={geofence.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900">{geofence.name}</h3>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          geofence.type === 'CIRCLE' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {geofence.type}
                        </span>
                        <Button
                          onClick={() => deleteMutation.mutate(geofence.id)}
                          size="sm"
                          variant="danger"
                          disabled={deleteMutation.isPending}
                        >
                          <TrashIcon className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Geometry Info */}
                    <div className="mb-3">
                      {geofence.type === 'CIRCLE' && geofence.geometry.center && (
                        <div className="text-sm text-gray-600">
                          <div>Center: {geofence.geometry.center.latitude.toFixed(6)}, {geofence.geometry.center.longitude.toFixed(6)}</div>
                          <div>Radius: {geofence.geometry.radius} meters</div>
                        </div>
                      )}
                      {geofence.type === 'POLYGON' && geofence.geometry.coordinates && (
                        <div className="text-sm text-gray-600">
                          <div>Polygon: {geofence.geometry.totalPoints} points</div>
                          <div className="font-mono text-xs">
                            Area covering: {geofence.geometry.coordinates[0]?.latitude.toFixed(4)}, {geofence.geometry.coordinates[0]?.longitude.toFixed(4)} to{' '}
                            {geofence.geometry.coordinates[2]?.latitude.toFixed(4)}, {geofence.geometry.coordinates[2]?.longitude.toFixed(4)}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Features */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {geofence.speeding.enabled && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                          Speed Limit: {geofence.speeding.threshold} km/h
                        </span>
                      )}

                      {geofence.schedule.allDay ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <ClockIcon className="w-3 h-3 mr-1" />
                          24/7 Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <ClockIcon className="w-3 h-3 mr-1" />
                          Scheduled: {geofence.schedule.timePeriods[0]?.start}-{geofence.schedule.timePeriods[0]?.end}
                        </span>
                      )}
                    </div>

                    <Button
                      onClick={() => setSelectedGeofence(geofence)}
                      size="sm"
                      variant="secondary"
                      className="w-full"
                    >
                      <Cog6ToothIcon className="w-4 h-4 mr-2" />
                      Manage Device Bindings
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MapIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Geofences Created</h3>
                <p className="text-gray-600 mb-6">Create your first geofence to start monitoring vehicle boundaries</p>
                <Button onClick={() => setShowCreateForm(true)}>
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Create First Geofence
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Quick Setup Templates */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Quick Setup Templates</h2>
            <p className="text-gray-600 text-sm mt-1">Pre-configured geofences for common school scenarios</p>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  name: 'School Zone',
                  type: 'CIRCLE',
                  radius: 300,
                  speedLimit: 20,
                  schedule: '06:00,18:00',
                  description: 'Main school campus with reduced speed limit during school hours',
                  icon: '🏫'
                },
                {
                  name: 'Bus Stop',
                  type: 'CIRCLE',
                  radius: 50,
                  speedLimit: 10,
                  schedule: '07:00,08:00;15:00,16:00',
                  description: 'Student pickup/dropoff points with morning and afternoon schedules',
                  icon: '🚌'
                },
                {
                  name: 'Residential Area',
                  type: 'POLYGON',
                  speedLimit: 30,
                  schedule: '',
                  description: '24/7 monitored residential neighborhood with speed restrictions',
                  icon: '🏘️'
                },
                {
                  name: 'Highway Restriction',
                  type: 'POLYGON',
                  speedLimit: 0, // No speeding restrictions
                  schedule: '',
                  description: 'Restricted area where school vehicles are not allowed',
                  icon: '⚠️'
                }
              ].map((template, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="text-center mb-3">
                    <div className="text-2xl mb-2">{template.icon}</div>
                    <h3 className="font-medium text-gray-900">{template.name}</h3>
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium mt-1 ${
                      template.type === 'CIRCLE' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {template.type}
                    </span>
                  </div>

                  <div className="space-y-2 text-xs text-gray-600 mb-4">
                    {template.radius && <div>Radius: {template.radius}m</div>}
                    {template.speedLimit > 0 && <div>Speed Limit: {template.speedLimit} km/h</div>}
                    {template.schedule && <div>Schedule: {template.schedule}</div>}
                  </div>

                  <p className="text-xs text-gray-500 mb-4">{template.description}</p>

                  <Button
                    onClick={() => {
                      // Pre-fill form with template data
                      reset({
                        geofencename: template.name,
                        geofencetype: template.type === 'CIRCLE' ? '2' : '3',
                        radius: template.radius,
                        geofencespeeding: template.speedLimit > 0 ? template.speedLimit.toString() : '',
                        geofenceusetimes: template.schedule,
                        zoneType: template.name.includes('School') ? 'SCHOOL' :
                                 template.name.includes('Bus') ? 'PICKUP_POINT' :
                                 template.name.includes('Highway') ? 'RESTRICTED_AREA' : 'SPEED_ZONE',
                        description: template.description
                      });
                      setShowCreateForm(true);
                    }}
                    size="sm"
                    variant="secondary"
                    className="w-full"
                  >
                    Use Template
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Create Form Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowCreateForm(false)} />

              <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">Create New Geofence</h3>

                  <form onSubmit={handleSubmit((data) => createMutation.mutate(data))} className="space-y-6">
                    {/* Basic Info */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Geofence Name *
                      </label>
                      <Controller
                        name="geofencename"
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder="e.g., Main School Zone, Bus Stop A"
                            error={errors.geofencename?.message}
                          />
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Zone Type
                        </label>
                        <Controller
                          name="zoneType"
                          control={control}
                          render={({ field }) => (
                            <select
                              {...field}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="SCHOOL">School Campus</option>
                              <option value="PICKUP_POINT">Bus Stop/Pickup Point</option>
                              <option value="SPEED_ZONE">Speed Limit Zone</option>
                              <option value="RESTRICTED_AREA">Restricted Area</option>
                            </select>
                          )}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Geofence Type
                        </label>
                        <Controller
                          name="geofencetype"
                          control={control}
                          render={({ field }) => (
                            <select
                              {...field}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="2">Circle</option>
                              <option value="3">Polygon</option>
                            </select>
                          )}
                        />
                      </div>
                    </div>

                    {/* Circle Configuration */}
                    {watchGeofenceType === '2' && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Circle Configuration</h4>

                        {/* Preset Locations */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Quick Locations
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            {presetLocations.map((preset, index) => (
                              <Button
                                key={index}
                                type="button"
                                onClick={() => handlePresetLocation(preset)}
                                size="sm"
                                variant="secondary"
                                className="text-xs"
                              >
                                {preset.name}
                              </Button>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Latitude *
                            </label>
                            <Controller
                              name="centerLatitude"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="number"
                                  step="0.000001"
                                  placeholder="-1.286389"
                                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                />
                              )}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Longitude *
                            </label>
                            <Controller
                              name="centerLongitude"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="number"
                                  step="0.000001"
                                  placeholder="36.817223"
                                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                />
                              )}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Radius (meters) *
                            </label>
                            <Controller
                              name="radius"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="number"
                                  min="100"
                                  max="5000"
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 500)}
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Advanced Settings */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Advanced Settings</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Speed Limit (km/h)
                          </label>
                          <Controller
                            name="geofencespeeding"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                type="number"
                                min="10"
                                max="400"
                                placeholder="Leave empty for no speed limit"
                              />
                            )}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Active Hours
                          </label>
                          <Controller
                            name="geofenceusetimes"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="07:00,17:00 or leave empty for 24/7"
                              />
                            )}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Format: HH:mm,HH:mm for single period or HH:mm,HH:mm;HH:mm,HH:mm for multiple periods
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <textarea
                            {...field}
                            rows={3}
                            placeholder="Describe the purpose and rules for this geofence..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        )}
                      />
                    </div>

                    <div className="flex items-center justify-end space-x-4 pt-6">
                      <Button
                        type="button"
                        onClick={() => setShowCreateForm(false)}
                        variant="secondary"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={!isValid || createMutation.isPending}
                      >
                        {createMutation.isPending ? (
                          <>
                            <LoadingSpinner size="sm" className="mr-2" />
                            Creating...
                          </>
                        ) : (
                          'Create Geofence'
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Use Case Examples */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center mb-4">
            <MapIcon className="w-6 h-6 text-purple-600 mr-3" />
            <h3 className="text-lg font-semibold text-purple-900">Geofence Use Cases for Schools</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-purple-900 mb-2">🏫 School Campus Safety</h4>
                <ul className="text-sm text-purple-800 space-y-1">
                  <li>• Create 300m radius around school with 20 km/h speed limit</li>
                  <li>• Active during school hours (7:00 AM - 6:00 PM)</li>
                  <li>• Alert parents when school bus enters/exits campus</li>
                  <li>• Automatically notify security of unauthorized vehicles</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-purple-900 mb-2">🚌 Bus Stop Management</h4>
                <ul className="text-sm text-purple-800 space-y-1">
                  <li>• 50m radius around each bus stop</li>
                  <li>• Active during pickup (7:00-8:00 AM) and dropoff (3:00-4:00 PM)</li>
                  <li>• Send SMS to parents when bus arrives at their stop</li>
                  <li>• Monitor student boarding and ensure safety</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-purple-900 mb-2">⚠️ Restricted Areas</h4>
                <ul className="text-sm text-purple-800 space-y-1">
                  <li>• Polygon boundaries around dangerous areas</li>
                  <li>• Immediately stop engine if vehicle enters restricted zone</li>
                  <li>• Alert school administration and emergency contacts</li>
                  <li>• Log violations for safety compliance reports</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-purple-900 mb-2">📍 Route Optimization</h4>
                <ul className="text-sm text-purple-800 space-y-1">
                  <li>• Monitor adherence to planned bus routes</li>
                  <li>• Alert when vehicles deviate from approved paths</li>
                  <li>• Optimize pickup sequences based on traffic patterns</li>
                  <li>• Ensure efficient fuel usage and on-time arrivals</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeofenceManagement;
